import ProjRef from "@/shared/components/ProjRef";
import { useEffect, useState, useCallback, useMemo } from "react";
import api, { fmtErr } from "@/shared/lib/api";
import { useAuth } from "@/shared/lib/auth";
import {
  Layers, Plus, Sparkles, ArrowLeft, ChevronRight, ChevronDown, FolderOpen, Pencil, Check, X,
  User, Trash2, CheckCircle2, Clock, Eye, MessageSquareWarning, Boxes, Ruler, Lock, Link2,
} from "lucide-react";
import { toast } from "sonner";
import { ScopeChangeActions, ScopeChangeRequests } from "@/features/boq/components/ScopeChanges";

/* CSM Sub-scope workspace (Msg 909).
   Project list → project detail (master-detail). For each major scope line the
   CSM sub-classifies it into walls/glasses, names + groups them, and assigns a
   designer + brief PER WALL. Assigning a designer flows the wall straight into
   the Design Review pipeline. Project BOQ page still shows live progress. */

const DSTATE = {
  unassigned: { label: "Assign designer", dot: "bg-amber-400",   chip: "bg-amber-100 text-amber-800 border-amber-300",      icon: User },
  assigned:   { label: "With designer",   dot: "bg-blue-400",    chip: "bg-blue-100 text-blue-700 border-blue-300",         icon: Clock },
  csm_review: { label: "Review needed",   dot: "bg-amber-500",   chip: "bg-amber-100 text-amber-800 border-amber-300",      icon: Eye },
  with_client:{ label: "With client",     dot: "bg-blue-400",    chip: "bg-blue-100 text-blue-700 border-blue-300",         icon: Clock },
  changes:    { label: "Changes",         dot: "bg-rose-400",    chip: "bg-rose-100 text-rose-700 border-rose-300",         icon: MessageSquareWarning },
  approved:   { label: "Approved",        dot: "bg-emerald-500", chip: "bg-emerald-100 text-emerald-700 border-emerald-300", icon: CheckCircle2 },
};

export default function SubScope() {
  const [projects, setProjects] = useState(null);
  const [view, setView] = useState({ code: null, bid: null });

  const load = useCallback(() => {
    api.get("/sub-scope")
      .then((r) => setProjects(r.data.projects || []))
      .catch((e) => { toast.error(fmtErr(e.response?.data?.detail) || "Failed to load"); setProjects([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const active = view.code ? (projects || []).find((p) => p.project_code === view.code) : null;
  const openProject = (code) => {
    const p = (projects || []).find((x) => x.project_code === code);
    const first = p?.scope_lines[0];
    setView({ code, bid: first?.boq_id || null });
  };

  return (
    <div className="p-5 sm:p-8 max-w-6xl mx-auto" data-testid="sub-scope-page">
      {projects === null ? (
        <div className="py-24 text-center text-zinc-400">Loading…</div>
      ) : active ? (
        <ProjectView project={active} selectedBid={view.bid}
                     onSelect={(bid) => setView({ code: active.project_code, bid })}
                     onBack={() => setView({ code: null, bid: null })} onDone={load} />
      ) : (
        <ProjectList projects={projects} onOpen={openProject} />
      )}
    </div>
  );
}

function ProjectList({ projects, onOpen }) {
  const totals = projects.reduce((a, p) => ({
    walls: a.walls + p.total_walls, unassigned: a.unassigned + p.unassigned_walls,
  }), { walls: 0, unassigned: 0 });
  return (
    <>
      <div className="mb-8">
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-orange-600 font-semibold">Client Success</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Sub-scope</h1>
        <p className="text-zinc-500 mt-2 text-sm sm:text-base max-w-2xl">
          Break each scope line into walls/glasses, name and group them, then assign a designer with a brief — straight from here.
        </p>
        {projects.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <Stat label="Walls to assign a designer" value={totals.unassigned} tone="amber" />
            <Stat label="Walls total" value={totals.walls} tone="zinc" />
            <Stat label="Projects" value={projects.length} tone="zinc" />
          </div>
        )}
      </div>
      {projects.length === 0 ? (
        <div className="py-24 text-center" data-testid="ss-empty">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-orange-50 mb-4">
            <Sparkles className="w-7 h-7 text-orange-400" strokeWidth={1.5} />
          </div>
          <div className="font-display text-xl font-bold">No projects yet</div>
          <p className="text-zinc-500 mt-1 text-sm">Projects you're assigned to as CSM will show up here.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {projects.map((p) => (
            <button key={p.project_code} onClick={() => onOpen(p.project_code)} data-testid={`ss-project-card-${p.project_code}`}
                    className="group text-left bg-white border border-zinc-200 rounded-xl p-5 shadow-sm hover:shadow-md hover:-translate-y-1 hover:border-orange-300 transition-all">
              <div className="flex items-center justify-between">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-orange-50 text-orange-600"><FolderOpen className="w-5 h-5" strokeWidth={1.5} /></div>
                <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-orange-500 transition-colors" strokeWidth={1.5} />
              </div>
              <div className="font-display text-lg font-bold tracking-tight mt-4 truncate">{p.project_name}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400"><ProjRef code={p.project_code} /></div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {p.unassigned_walls > 0 && <Pill tone="amber">{p.unassigned_walls} to assign</Pill>}
                <Pill tone="zinc">{p.total_walls} wall{p.total_walls !== 1 ? "s" : ""}</Pill>
                <Pill tone="zinc">{p.scope_lines.length} scope line{p.scope_lines.length !== 1 ? "s" : ""}</Pill>
              </div>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

function ProjectView({ project, selectedBid, onSelect, onBack, onDone }) {
  const selected = project.scope_lines.find((l) => l.boq_id === selectedBid) || project.scope_lines[0];
  return (
    <div data-testid={`ss-project-view-${project.project_code}`}>
      <button onClick={onBack} data-testid="ss-back-btn"
              className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-orange-600 transition-colors mb-4">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> All projects
      </button>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-orange-600 font-semibold"><ProjRef code={project.project_code} /></div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1">{project.project_name}</h1>
          <p className="text-zinc-500 mt-1 text-sm">{project.scope_lines.length} scope line{project.scope_lines.length !== 1 ? "s" : ""} · pick one to sub-classify.</p>
        </div>
        <ScopeChangeActions code={project.project_code} lines={project.scope_lines} onDone={onDone} />
      </div>
      <ScopeChangeRequests code={project.project_code} onChanged={onDone} />
      {project.scope_lines.length === 0 ? (
        <div className="py-16 text-center text-zinc-400 text-sm">No scope lines on this project yet.</div>
      ) : (
        <div className="grid lg:grid-cols-[minmax(260px,320px)_1fr] gap-6 items-start">
          <div className="bg-white border border-zinc-200 rounded-xl divide-y divide-zinc-100 overflow-hidden lg:sticky lg:top-20">
            {project.scope_lines.map((l) => (
              <LineRow key={l.boq_id} l={l} active={l.boq_id === selected?.boq_id} onClick={() => onSelect(l.boq_id)} />
            ))}
          </div>
          {selected && (
            <ScopeWorkspace key={selected.boq_id} code={project.project_code} line={selected}
                            designers={project.designers || []} onDone={onDone} />
          )}
        </div>
      )}
    </div>
  );
}

function LineRow({ l, active, onClick }) {
  return (
    <button onClick={onClick} data-testid={`ss-line-row-${l.boq_id}`}
            className={`w-full flex items-center gap-3 px-3.5 py-3 text-left transition-colors ${active ? "bg-orange-50/70" : "hover:bg-zinc-50"} ${l.descoped ? "opacity-50" : ""}`}>
      <div className="w-9 h-9 rounded-lg bg-zinc-50 border border-zinc-200 flex items-center justify-center flex-shrink-0">
        <Boxes className="w-4 h-4 text-zinc-400" strokeWidth={1.5} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-zinc-900 truncate">
          {l.title}
          {l.descoped && (
            <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-bold uppercase tracking-wider"
                  data-testid={`ss-descoped-chip-${l.boq_id}`}>Descoped</span>
          )}
          {!l.descoped && l.needs_commercials && (
            <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold uppercase tracking-wider"
                  data-testid={`ss-commercials-chip-${l.boq_id}`}>Commercials pending</span>
          )}
        </div>
        <div className="text-[11px] text-zinc-500 mt-0.5">
          {l.wall_count} wall{l.wall_count !== 1 ? "s" : ""}
          {l.quantity ? ` · ${l.quantity} ${l.unit}` : ""}
          {l.unassigned_count > 0 && <span className="text-amber-600"> · {l.unassigned_count} to assign</span>}
        </div>
      </div>
      <ChevronRight className={`w-4 h-4 flex-shrink-0 ${active ? "text-orange-500" : "text-zinc-300"}`} strokeWidth={1.5} />
    </button>
  );
}

function ScopeWorkspace({ code, line, designers, onDone }) {
  const groupList = line.groups || [];
  const wallsByGroup = useMemo(() => {
    const map = {};
    for (const w of line.walls) (map[w.group || ""] ||= []).push(w);
    return map;
  }, [line.walls]);
  const allGroups = useMemo(() => [...new Set([...groupList])], [groupList]);
  const ungrouped = wallsByGroup[""] || [];
  // Drill-down: one group open at a time. Default to the first group (or
  // ungrouped if there are no groups yet).
  const [openKey, setOpenKey] = useState(() => allGroups[0] ?? "__ungrouped__");
  const toggle = (key) => setOpenKey((cur) => (cur === key ? null : key));

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden" data-testid={`ss-workspace-${line.boq_id}`}>
      <div className="p-5 sm:p-6 border-b border-zinc-100">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-orange-600 font-semibold">Major scope</div>
        <h3 className="font-display text-xl font-bold tracking-tight mt-0.5">{line.title}</h3>
        {line.description && <p className="text-sm text-zinc-500 mt-1">{line.description}</p>}
        <div className="text-[11px] text-zinc-400 mt-1">
          {line.unit ? `Unit: ${line.unit} · ` : ""}{line.wall_count} wall{line.wall_count !== 1 ? "s" : ""}
          {allGroups.length > 0 && ` · ${allGroups.length} group${allGroups.length !== 1 ? "s" : ""}`}
        </div>
      </div>

      {line.descoped && (
        <div className="mx-5 mt-4 px-4 py-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm font-medium"
             data-testid={`ss-descoped-banner-${line.boq_id}`}>
          This scope has been de-scoped — it is excluded from the pipeline and billing. Use Re-scope (top right) to restore quantity.
        </div>
      )}
      {/* Step 1 — create a group */}
      <div className="p-5 sm:p-6 border-b border-zinc-100 bg-zinc-50/40">
        <CreateGroup code={code} bid={line.boq_id} existing={allGroups} onDone={onDone} />
      </div>

      {/* Shared design sets — one design file for items across floors/groups */}
      <ShareDesignBar code={code} bid={line.boq_id}
                      walls={line.walls || []}
                      onDone={onDone} />

      {/* Step 2 — groups list (drill down to reveal walls) */}
      <div className="p-3 sm:p-4 space-y-2.5">
        {allGroups.length === 0 && ungrouped.length === 0 && (
          <p className="px-2 py-6 text-center text-sm text-zinc-400 italic">
            Create a group above, then add walls into it — or add ungrouped walls below.
          </p>
        )}
        {allGroups.map((g) => (
          <GroupAccordion key={g} code={code} bid={line.boq_id} group={g}
                          walls={wallsByGroup[g] || []} groups={allGroups}
                          designers={designers} open={openKey === g}
                          onToggle={() => toggle(g)} onDone={onDone} />
        ))}
        <GroupAccordion code={code} bid={line.boq_id} group=""
                        walls={ungrouped} groups={allGroups} designers={designers}
                        open={openKey === "__ungrouped__"}
                        onToggle={() => toggle("__ungrouped__")} onDone={onDone} />
      </div>
    </div>
  );
}

function CreateGroup({ code, bid, existing, onDone }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  async function create() {
    const n = name.trim();
    if (!n) { toast.error("Name the group first"); return; }
    if (existing.includes(n)) { toast.error("That group already exists"); return; }
    setSaving(true);
    try {
      await api.post(`/projects/${code}/boq/${bid}/wall-groups`, { name: n });
      toast.success(`Group "${n}" created — now add walls into it.`); setName(""); onDone();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="flex-1 min-w-[200px]">
        <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 flex items-center gap-1">
          <Layers className="w-3 h-3" strokeWidth={1.5} /> Create a sub-category (group)
        </label>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Lobby walls, Reception walls"
               onKeyDown={(e) => { if (e.key === "Enter") create(); }}
               className="mt-1 w-full h-9 rounded-md border border-zinc-200 px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
               data-testid={`ss-create-group-input-${bid}`} />
      </div>
      <button onClick={create} disabled={saving} data-testid={`ss-create-group-btn-${bid}`}
              className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 disabled:opacity-50">
        <Plus className="w-4 h-4" strokeWidth={1.5} /> {saving ? "Creating…" : "Create group"}
      </button>
    </div>
  );
}

function GroupAccordion({ code, bid, group, walls, groups, designers, open, onToggle, onDone }) {
  const isUngrouped = !group;
  const toAssign = walls.filter((w) => !w.designer_id).length;
  const { user } = useAuth();
  const canRename = ["admin", "csm"].includes(user?.role);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState(group);
  async function removeGroup(e) {
    e.stopPropagation();
    if (!window.confirm(`Remove the group "${group}"? Its ${walls.length} wall(s) become ungrouped (the walls are kept).`)) return;
    try {
      await api.delete(`/projects/${code}/boq/${bid}/wall-groups?name=${encodeURIComponent(group)}`);
      toast.success("Group removed."); onDone();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }
  async function renameGroup() {
    const n = (newName || "").trim();
    if (!n || n === group) { setRenaming(false); setNewName(group); return; }
    try {
      await api.patch(`/projects/${code}/boq/${bid}/wall-groups/rename`, { old_name: group, new_name: n });
      toast.success(`Group renamed to "${n}".`); setRenaming(false); onDone();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }
  return (
    <div data-testid={`ss-group-${bid}-${group || "ungrouped"}`}
         className={`rounded-xl border overflow-hidden transition-colors ${open ? "border-orange-300 shadow-sm" : "border-zinc-200 hover:border-orange-200"}`}>
      {/* Header row — click to drill in */}
      <button onClick={onToggle} data-testid={`ss-group-toggle-${bid}-${group || "ungrouped"}`}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${open ? "bg-orange-50/70" : "bg-white hover:bg-zinc-50"}`}>
        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 ${isUngrouped ? "bg-zinc-100 text-zinc-400" : "bg-orange-100 text-orange-600"}`}>
          <Layers className="w-4 h-4" strokeWidth={1.5} />
        </span>
        <div className="min-w-0 flex-1">
          {renaming ? (
            <span className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
              <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") renameGroup(); if (e.key === "Escape") { setRenaming(false); setNewName(group); } }}
                data-testid={`ss-rename-group-input-${bid}-${group}`}
                className="text-sm font-semibold text-zinc-900 border border-orange-300 rounded-md px-2 py-1 w-48 focus:outline-none focus:ring-2 focus:ring-orange-200" />
              <span role="button" onClick={renameGroup} data-testid={`ss-rename-group-save-${bid}-${group}`}
                className="text-emerald-600 hover:text-emerald-800"><Check className="w-4 h-4" /></span>
              <span role="button" onClick={() => { setRenaming(false); setNewName(group); }}
                className="text-zinc-400 hover:text-zinc-600"><X className="w-4 h-4" /></span>
            </span>
          ) : (
            <div className={`text-sm font-semibold truncate flex items-center gap-1.5 ${isUngrouped ? "text-zinc-600" : "text-zinc-900"}`}>
              {group || "Ungrouped"}
              {!isUngrouped && canRename && (
                <span role="button" onClick={(e) => { e.stopPropagation(); setNewName(group); setRenaming(true); }}
                  data-testid={`ss-rename-group-${bid}-${group}`} title="Rename group"
                  className="text-zinc-300 hover:text-orange-600 transition-colors"><Pencil className="w-3.5 h-3.5" /></span>
              )}
            </div>
          )}
          <div className="text-[11px] text-zinc-500 mt-0.5">
            {walls.length} wall{walls.length !== 1 ? "s" : ""}
            {toAssign > 0 && <span className="text-amber-600"> · {toAssign} to assign a designer</span>}
          </div>
        </div>
        {!isUngrouped && (
          <span onClick={removeGroup} role="button" tabIndex={0} data-testid={`ss-remove-group-${bid}-${group}`}
                className="text-[10px] font-mono uppercase tracking-wider text-zinc-300 hover:text-rose-600 transition-colors px-1">remove</span>
        )}
        <ChevronRight className={`w-4 h-4 flex-shrink-0 transition-transform ${open ? "rotate-90 text-orange-500" : "text-zinc-300"}`} strokeWidth={1.5} />
      </button>

      {/* Drill-down body — the walls in this group */}
      {open && (
        <div className="px-3 sm:px-4 pb-4 pt-1 bg-white border-t border-zinc-100 space-y-3">
          {walls.length === 0 && (
            <p className="text-sm text-zinc-400 italic pt-2">No walls here yet — add some below.</p>
          )}
          {walls.map((w) => (
            <WallEditor key={w.boq_id} code={code} w={w} designers={designers} groups={groups} onDone={onDone} />
          ))}
          <div className="pt-1">
            <AddWallsToGroup code={code} bid={bid} group={group} hasAny={walls.length > 0} onDone={onDone} />
          </div>
        </div>
      )}
    </div>
  );
}

function ShareDesignBar({ code, bid, walls, onDone }) {
  const { user } = useAuth();
  const canLink = ["admin", "csm"].includes(user?.role);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(new Set());
  const [saving, setSaving] = useState(false);
  if (!canLink || walls.length < 2) return null;
  const free = walls.filter((w) => !w.design_link_lead);
  const sets = {};
  walls.forEach((w) => { if (w.design_link_id) (sets[w.design_link_id] ||= []).push(w); });
  const toggleSel = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  async function link() {
    setSaving(true);
    try {
      await api.post(`/projects/${code}/boq/${bid}/design-link`, { wall_ids: [...sel] });
      toast.success(`${sel.size} items now share one design file.`);
      setSel(new Set()); setOpen(false); onDone();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }
  async function unlink(lid) {
    if (!window.confirm("Unlink this shared-design set? Each item gets its own design again.")) return;
    try { await api.delete(`/projects/${code}/design-links/${lid}`); toast.success("Unlinked."); onDone(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }
  return (
    <div className="px-5 sm:px-6 py-4 border-b border-zinc-100 bg-indigo-50/30" data-testid={`ss-sharebar-${bid}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-indigo-900">
          <span className="font-semibold">Shared design sets</span> — link items across floors so the designer uploads <b>one</b> file (one approval, one production file).
        </div>
        <button onClick={() => setOpen((o) => !o)} data-testid={`ss-sharebar-toggle-${bid}`}
                className="text-xs font-semibold text-indigo-700 hover:text-indigo-900">
          {open ? "Close" : "+ Link items as one design"}
        </button>
      </div>
      {Object.entries(sets).map(([lid, ws]) => (
        <div key={lid} className="mt-2 flex items-center gap-2 flex-wrap text-[11px]" data-testid={`ss-linkset-${lid}`}>
          <span className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-semibold">1 design × {ws[0]?.design_link_count || ws.length}</span>
          <span className="text-zinc-500 truncate">{ws.map((w) => w.label || w.wall_no).join(", ")}</span>
          <button onClick={() => unlink(lid)} className="text-rose-500 hover:text-rose-700 font-medium"
                  data-testid={`ss-unlink-${lid}`}>Unlink</button>
        </div>
      ))}
      {open && (
        <div className="mt-3 space-y-1.5">
          {free.length < 2 && <p className="text-[11px] text-zinc-400 italic">Not enough unlinked items left.</p>}
          {free.map((w) => (
            <label key={w.boq_id} className="flex items-center gap-2 text-xs text-zinc-700 cursor-pointer">
              <input type="checkbox" checked={sel.has(w.boq_id)} onChange={() => toggleSel(w.boq_id)}
                     data-testid={`ss-linkpick-${w.boq_id}`} className="accent-indigo-600" />
              {w.label || w.wall_no}{w.group ? <span className="text-zinc-400">· {w.group}</span> : null}
            </label>
          ))}
          {free.length >= 2 && (
            <button onClick={link} disabled={saving || sel.size < 2} data-testid={`ss-link-save-${bid}`}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 disabled:opacity-40">
              <Link2 className="w-3.5 h-3.5" strokeWidth={1.5} />
              {saving ? "Linking…" : `Link ${sel.size || ""} items as one design`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function AddWallsToGroup({ code, bid, group, hasAny, onDone }) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState("");
  const [kind, setKind] = useState("wall");   // "wall" | "glass"
  const [batch, setBatch] = useState(false);  // single shared design/pre-press/production file for all N
  const [saving, setSaving] = useState(false);
  const kindLabel = kind === "glass" ? "glass panel" : kind === "others" ? "item"
    : kind === "signage" ? "signage" : kind === "interactive" ? "interactive wall" : "wall";
  async function add() {
    const n = parseInt(count, 10);
    if (!n || n < 1) { toast.error("How many walls / glass / items?"); return; }
    setSaving(true);
    try {
      await api.post(`/projects/${code}/boq/${bid}/walls`, { count: n, group, kind, batch });
      toast.success(batch
        ? `Batch of ${n} pcs added${group ? ` to "${group}"` : ""} — one shared file, no site measurement.`
        : `${n} ${kindLabel}${n > 1 ? "s" : ""} added${group ? ` to "${group}"` : ""}. PSM notified to measure.`);
      setCount(""); setBatch(false); setOpen(false); onDone();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid={`ss-add-walls-toggle-${bid}-${group || "ungrouped"}`}
              className="inline-flex items-center gap-1.5 text-sm text-orange-700 hover:text-orange-800 font-medium transition-colors">
        <Plus className="w-4 h-4" strokeWidth={1.5} /> Add walls / glass / others{group ? ` to "${group}"` : ""}
      </button>
    );
  }
  return (
    <div className="w-full" data-testid={`ss-add-walls-form-${bid}-${group || "ungrouped"}`}>
     <div className="flex flex-wrap items-end gap-2">
      <div>
        <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Type</label>
        <div className="mt-1 inline-flex rounded-md border border-zinc-200 overflow-hidden" data-testid={`ss-add-kind-${bid}-${group || "ungrouped"}`}>
          <button type="button" onClick={() => setKind("wall")} data-testid={`ss-add-kind-wall-${bid}-${group || "ungrouped"}`}
                  className={`h-9 px-3 text-sm font-medium transition-colors ${kind === "wall" ? "bg-orange-500 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}>
            Wall
          </button>
          <button type="button" onClick={() => setKind("glass")} data-testid={`ss-add-kind-glass-${bid}-${group || "ungrouped"}`}
                  className={`h-9 px-3 text-sm font-medium border-l border-zinc-200 transition-colors ${kind === "glass" ? "bg-sky-500 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}>
            Glass panel
          </button>
          <button type="button" onClick={() => setKind("others")} data-testid={`ss-add-kind-others-${bid}-${group || "ungrouped"}`}
                  className={`h-9 px-3 text-sm font-medium border-l border-zinc-200 transition-colors ${kind === "others" ? "bg-violet-500 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}>
            Others
          </button>
          <button type="button" onClick={() => setKind("signage")} data-testid={`ss-add-kind-signage-${bid}-${group || "ungrouped"}`}
                  className={`h-9 px-3 text-sm font-medium border-l border-zinc-200 transition-colors ${kind === "signage" ? "bg-emerald-500 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}>
            Signage
          </button>
          <button type="button" onClick={() => setKind("interactive")} data-testid={`ss-add-kind-interactive-${bid}-${group || "ungrouped"}`}
                  className={`h-9 px-3 text-sm font-medium border-l border-zinc-200 transition-colors ${kind === "interactive" ? "bg-rose-500 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}>
            Interactive Wall
          </button>
        </div>
      </div>
      <div className="w-28">
        <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{batch ? "How many pcs" : "How many"}</label>
        <input type="number" min="1" value={count} onChange={(e) => setCount(e.target.value)} autoFocus placeholder={batch ? "50" : "3"}
               onKeyDown={(e) => { if (e.key === "Enter") add(); }}
               className="mt-1 w-full h-9 rounded-md border border-zinc-200 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
               data-testid={`ss-add-walls-count-${bid}-${group || "ungrouped"}`} />
      </div>
      <button onClick={add} disabled={saving} data-testid={`ss-add-walls-confirm-${bid}-${group || "ungrouped"}`}
              className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 disabled:opacity-50">
        <Plus className="w-4 h-4" strokeWidth={1.5} /> {saving ? "Adding…" : (batch ? "Add batch" : `Add ${kindLabel}`)}
      </button>
      <button onClick={() => setOpen(false)} className="h-9 px-3 text-sm text-zinc-500 hover:text-zinc-800">Cancel</button>
     </div>
     <label className="mt-2 inline-flex items-start gap-2 text-xs text-zinc-600 cursor-pointer" data-testid={`ss-add-batch-${bid}-${group || "ungrouped"}`}>
       <input type="checkbox" checked={batch} onChange={(e) => setBatch(e.target.checked)} className="mt-0.5"
              data-testid={`ss-add-batch-checkbox-${bid}-${group || "ungrouped"}`} />
       <span>
         <b>Single shared file batch</b> — one design, one client approval, one pre-press &amp; one production file for all pieces (e.g. 50 room signages cut from one sheet). <span className="text-zinc-400">No site measurement needed; drop a pin per piece on the floorplan.</span>
       </span>
     </label>
    </div>
  );
}

function WallEditor({ code, w, designers, groups, onDone }) {
  const meta = DSTATE[w.design_state] || DSTATE.unassigned;
  const Icon = meta.icon;
  const [renaming, setRenaming] = useState(false);
  const [nameVal, setNameVal] = useState(w.wall_name || "");
  const [noVal, setNoVal] = useState(w.wall_no || "");
  const [designerId, setDesignerId] = useState(w.designer_id || "");
  const [brief, setBrief] = useState(w.designer_brief || "");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(!w.designer_id);  // collapse assigned walls by default

  const dirty = designerId !== (w.designer_id || "") || brief !== (w.designer_brief || "");
  // Once a designer is assigned, the assignment locks (read-only) until the CSM
  // explicitly clicks "Change" — and changing requires a typed reason on save.
  const locked = !!w.designer_id && !editing;
  const designerName = w.designer_name || designers.find((d) => d.id === w.designer_id)?.name || "Assigned";

  async function saveName() {
    if (!noVal.trim()) { toast.error("The wall number can't be empty."); return; }
    try {
      await api.patch(`/projects/${code}/boq/${w.boq_id}/wall-name`, { wall_name: nameVal.trim(), wall_no: noVal.trim() });
      toast.success("Wall number & name saved."); setRenaming(false); onDone();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }
  async function moveGroup(val) {
    try {
      await api.patch(`/projects/${code}/boq/${w.boq_id}/wall-group`, { group: val });
      onDone();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }
  async function saveDesigner() {
    let reason = "";
    if (w.designer_id && designerId !== w.designer_id) {
      reason = window.prompt("Reason for changing the assigned designer?") || "";
      if (!reason.trim()) { toast.error("A reason is required to change the designer."); return; }
    }
    setSaving(true);
    try {
      await api.patch(`/projects/${code}/boq/${w.boq_id}/designer`, {
        designer_id: designerId, design_instruction: brief.trim(), reason: reason.trim(),
      });
      toast.success(designerId ? "Designer & brief saved." : "Designer cleared."); setEditing(false); onDone();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }
  async function removeWall() {
    const reason = window.prompt("Reason for removing this wall?");
    if (!reason || !reason.trim()) return;
    try {
      await api.delete(`/projects/${code}/boq/${w.boq_id}/wall?reason=${encodeURIComponent(reason.trim())}`);
      toast.success("Wall removed."); onDone();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }

  return (
    <div className="border border-zinc-200 rounded-xl p-4 bg-zinc-50/60" data-testid={`ss-wall-${w.boq_id}`}>
      <div className="flex items-start justify-between gap-3">
        <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`ss-wall-toggle-${w.boq_id}`}
                className="mt-0.5 text-zinc-400 hover:text-zinc-700 transition-colors shrink-0">
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "" : "-rotate-90"}`} strokeWidth={1.5} />
        </button>
        <div className="min-w-0 flex-1">
          {renaming ? (
            <div className="flex flex-wrap items-center gap-2">
              <input value={noVal} onChange={(e) => setNoVal(e.target.value)} autoFocus placeholder="e.g. Wall 7"
                     title="Wall / glass number"
                     onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setRenaming(false); }}
                     className="h-8 w-[110px] rounded-md border border-zinc-200 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                     data-testid={`ss-wall-no-input-${w.boq_id}`} />
              <input value={nameVal} onChange={(e) => setNameVal(e.target.value)} placeholder="Custom name (optional)"
                     onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setRenaming(false); }}
                     className="h-8 max-w-[200px] rounded-md border border-zinc-200 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                     data-testid={`ss-wall-name-input-${w.boq_id}`} />
              <button onClick={saveName} data-testid={`ss-wall-name-save-${w.boq_id}`} className="text-[11px] font-medium text-emerald-700 hover:text-emerald-800">Save</button>
              <button onClick={() => { setRenaming(false); setNameVal(w.wall_name || ""); setNoVal(w.wall_no || ""); }} className="text-[11px] text-zinc-400 hover:text-zinc-600">Cancel</button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <h4 className="text-sm font-semibold text-zinc-900" data-testid={`ss-wall-label-${w.boq_id}`}>{w.label}</h4>
              {w.design_link_id && (
                <span title="Shared-design set — one file covers all linked items" data-testid={`ss-wall-linkchip-${w.boq_id}`}
                      className="inline-flex items-center gap-1 px-1.5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-semibold whitespace-nowrap">
                  <Link2 className="w-3 h-3" strokeWidth={1.5} />
                  {w.design_link_lead === w.boq_id ? `1 design × ${w.design_link_count}` : "shared design"}
                </span>
              )}
              <button onClick={() => { setNameVal(w.wall_name || ""); setNoVal(w.wall_no || ""); setRenaming(true); }} data-testid={`ss-wall-name-edit-${w.boq_id}`}
                      title="Edit wall number / name" className="text-zinc-300 hover:text-orange-600 transition-colors">
                <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            </div>
          )}
          <div className="flex items-center gap-2 mt-1">
            {w.measured ? (
              <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700"><Ruler className="w-3 h-3" strokeWidth={1.5} /> Measured</span>
            ) : (
              <span className="text-[11px] text-zinc-400">Awaiting site measurement</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`inline-flex items-center gap-1 px-2 h-6 rounded-full text-[10px] font-medium border whitespace-nowrap ${meta.chip}`} data-testid={`ss-wall-state-${w.boq_id}`}>
            <Icon className="w-3 h-3" strokeWidth={1.5} /> {meta.label}
          </span>
          <button onClick={removeWall} data-testid={`ss-wall-remove-${w.boq_id}`} title="Remove wall" className="text-zinc-300 hover:text-rose-600 transition-colors">
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {open && (<>
      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        {/* Group — always editable (organisational only) */}
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Group</label>
          <select value={w.group || ""} onChange={(e) => moveGroup(e.target.value)}
                  className="mt-1 w-full h-9 rounded-md border border-zinc-200 px-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-300"
                  data-testid={`ss-wall-group-${w.boq_id}`}>
            <option value="">Ungrouped</option>
            {groups.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        {/* Designer — LOCKS once assigned; "Change" reopens it (reason required on save) */}
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Designer</label>
          {locked ? (
            <div className="mt-1 flex items-center justify-between gap-2 h-9 px-3 rounded-md border border-emerald-200 bg-emerald-50"
                 data-testid={`ss-wall-designer-locked-${w.boq_id}`}>
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-800 truncate">
                <Lock className="w-3 h-3 flex-shrink-0" strokeWidth={1.5} /> {designerName}
              </span>
              <button onClick={() => setEditing(true)} data-testid={`ss-wall-designer-change-${w.boq_id}`}
                      className="text-[11px] font-medium text-orange-600 hover:text-orange-700 flex-shrink-0">Change</button>
            </div>
          ) : (
            <select value={designerId} onChange={(e) => setDesignerId(e.target.value)}
                    className="mt-1 w-full h-9 rounded-md border border-zinc-200 px-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-300"
                    data-testid={`ss-wall-designer-${w.boq_id}`}>
              <option value="">— Unassigned —</option>
              {designers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}
        </div>
      </div>

      <div className="mt-3">
        <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Brief for the designer</label>
        {locked ? (
          <p className="mt-1 text-sm text-zinc-700 whitespace-pre-line bg-zinc-50 border border-zinc-200 rounded-md px-2.5 py-2 min-h-[2.25rem]"
             data-testid={`ss-wall-brief-locked-${w.boq_id}`}>
            {brief || <span className="text-zinc-400 italic">No brief added.</span>}
          </p>
        ) : (
          <textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={2}
                    placeholder="What's required for this wall — concept, copy, colours, references…"
                    className="mt-1 w-full text-sm rounded-md border border-zinc-200 px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-orange-300"
                    data-testid={`ss-wall-brief-${w.boq_id}`} />
        )}
      </div>

      {!locked && (
        designers.length === 0 ? (
          <p className="mt-2 text-[11px] text-amber-600">Add designers to the project team first to assign them here.</p>
        ) : (
          <div className="mt-2 flex justify-end gap-2">
            {editing && (
              <button onClick={() => { setEditing(false); setDesignerId(w.designer_id || ""); setBrief(w.designer_brief || ""); }}
                      className="inline-flex items-center h-9 px-4 rounded-md border border-zinc-200 text-zinc-600 text-sm font-medium hover:bg-zinc-50">
                Cancel
              </button>
            )}
            <button onClick={saveDesigner} disabled={saving || !dirty} data-testid={`ss-wall-save-${w.boq_id}`}
                    className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 disabled:opacity-40">
              {saving ? "Saving…" : "Save designer & brief"}
            </button>
          </div>
        )
      )}
      </>)}
    </div>
  );
}

function Stat({ label, value, tone }) {
  const cls = { amber: "bg-amber-50 text-amber-800 border-amber-200", zinc: "bg-zinc-100 text-zinc-700 border-zinc-200" }[tone];
  return <span className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-medium border ${cls}`}><span className="font-bold tabular-nums">{value}</span> {label}</span>;
}
function Pill({ tone, children }) {
  const cls = { amber: "bg-amber-100 text-amber-800", zinc: "bg-zinc-100 text-zinc-600" }[tone];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${cls}`}>{children}</span>;
}
